import { useEffect, useRef, useState } from "react";
import { Camera, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

// Camera QR/barcode scanning using the browser's BarcodeDetector when it exists;
// falls back to typing the bin code by hand (every phone can still work).
export const BinScanner = ({ onCode }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [on, setOn] = useState(false);
  const [note, setNote] = useState(null);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setOn(false);
  };

  useEffect(() => () => stop(), []);

  const start = async () => {
    if (!("BarcodeDetector" in window)) {
      setNote("This browser cannot scan — type the bin code instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setOn(true);
      setNote(null);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // eslint-disable-next-line no-undef
      const detector = new BarcodeDetector({ formats: ["qr_code", "code_128", "code_39", "ean_13"] });
      const tick = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const found = await detector.detect(videoRef.current);
          if (found.length) {
            onCode(String(found[0].rawValue).trim().toUpperCase());
            stop();
            return;
          }
        } catch { /* keep trying */ }
        setTimeout(tick, 400);
      };
      tick();
    } catch {
      setNote("No camera access — type the bin code instead.");
      stop();
    }
  };

  return (
    <div className="space-y-2" data-testid="bin-scanner">
      {on ? (
        <div className="relative">
          <video ref={videoRef} muted playsInline className="h-48 w-full bg-black object-cover" data-testid="bin-scanner-video" />
          <button data-testid="bin-scanner-stop" onClick={stop} className="absolute right-2 top-2 bg-white/90 p-1.5">
            <X size={14} />
          </button>
        </div>
      ) : (
        <Button variant="outline" className="w-full" data-testid="bin-scanner-start" onClick={start}>
          <Camera size={16} className="mr-1.5" /> Scan a bin label
        </Button>
      )}
      {note && <p data-testid="bin-scanner-note" className="text-xs text-slate-500">{note}</p>}
    </div>
  );
};
